/* eslint-disable no-undef */
import React, {
  useState,
  useRef,
  useLayoutEffect,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import {
  Labelbox,
  InfoTable,
  FrameBox,
  Table,
  Button,
  Modal,
  ContractInvoiceView,
  Spinner,
} from "../../components";
import SearchIcon from "@material-ui/icons/Search";
import { Dollar } from "../../images";
import { useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Paper } from "@material-ui/core";
import {
  AddCircleOutlineOutlined,
  Edit,
  ArrowBack,
  PrintRounded,
} from "@material-ui/icons";
import "./contract.scss";
import {
  INVOICE_ACTION,
  CONTRACT_ACTION,
  CLIENT_ACTION,
  EMPLOYEE_ACTION,
} from "../../redux/action";
import {
  Toaster,
  dateFormatter,
  dateTimeFormatter,
  dropdownName,
  wait,
  detailsBind,
  Loader,
  addressFormator,
  getDocumentBlob,
  cancelValidation,
  onSubmitValidation,
  checkValidation,
} from "../../utils/common";
import ValidationLibrary from "../../utils/validationfunction";
import { useReactToPrint } from "react-to-print";
import ContractPayment from "../contract_payment";
import {
  getInvoicePaymentStatusLabel,
  getInvoicePaymentMethodLabel,
  isInvoiceCancelled,
  formatInvoicePaidDateDisplay,
  getInvoicePaidDateRaw,
  paidDateToApiValue,
  shouldShowInvoicePaidDate,
} from "../../utils/invoiceStatusDisplay";
import { getClientInvoiceId } from "../../utils/invoiceIds";
import { syncEfilingPaymentStatusFromInvoice } from "../../utils/efilingApi";
import {
  billed_from_keys,
  billed_from_values,
  billed_to_keys,
  billed_to_business_values,
  billed_to_personal_values,
  editKeys,
  DISCOUNT_REFERRAL_OPTIONS,
} from "./contants";
import PaymentStatusModal from "../../components/PaymentStatusModal";
import { buildAssignedClientScopeParams } from "../../utils/staffListScope";
import { normalizeClientDetailCategory } from "../../utils/clientDetailsCategory";
import {
  getInvoiceListFilters,
  invoiceMatchesEmployeeIds,
} from "../../utils/invoiceListQueryParams";
import {
  isMaskedEmail,
  withUnmaskedBillToEmail,
} from "../../utils/clientEmailMasking";

/** Base options for invoice line item name; API may return labels not in this list. */
const INVOICE_LINE_ITEM_NAME_OPTIONS = [
  {
    id: "Tax Planning & Consulation",
    value: "Tax Planning & Consulation",
  },
  { id: "Individual Tax Service", value: "Individual Tax Service" },
  { id: "Individual Tax Services", value: "Individual Tax Services" },
  { id: "Business Tax Services", value: "Business Tax Services" },
  {
    id: "Business Formation Services",
    value: "Business Formation Services",
  },
  {
    id: "Bookkeeping and Payroll Services",
    value: "Bookkeeping and Payroll Services",
  },
  {
    id: "Misc. Tax and Compliance Services",
    value: "Misc. Tax and Compliance Services",
  },
];

const CreatePage = () => {
  const CONTRACT = {
    insertAPI: CONTRACT_ACTION.insertUpdateContract,
    deleteAPI: CONTRACT_ACTION.deleteContractById,
    getAPI: CONTRACT_ACTION.getAllContracts,
    label: "Contract",
    dateKey: "contract_date",
    primaryID: "client_contract_id",
    subID: "client_contract_item_id",
    no: "contract_no",
  };
  const INVOICE = {
    insertAPI: INVOICE_ACTION.insertUpdateInvoice,
    deleteAPI: INVOICE_ACTION.deleteInvoiceById,
    getAPI: INVOICE_ACTION.getAllInvoices,
    label: "Invoice",
    dateKey: "invoice_date",
    primaryID: "client_invoice_id",
    subID: "client_invoice_item_id",
    no: "invoice_no",
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const invoiceListFilters = useMemo(
    () => getInvoiceListFilters(location, searchParams),
    [location, searchParams],
  );

  const customRef = useRef(null);
  const divRef = useRef(null);
  const [clientDetails, setClientDetails] = useState(() => {
    if (!location.state) return null;
    const category = location.state.category || location.state.data?.category || "individual";
    return {
      ...location.state,
      category,
    };
  });
  const lockBilledPartySelection = useMemo(
    () =>
      Boolean(
        clientDetails?.fromClientDetails || clientDetails?.lockBilledParties,
      ),
    [clientDetails],
  );

  const fetchClientForEmail = useCallback(
    async (clientId) => {
      if (!clientId) return null;
      try {
        return await dispatch(CLIENT_ACTION.getClientById(clientId)).unwrap();
      } catch {
        return null;
      }
    },
    [dispatch],
  );

  const unmaskBillToEmail = useCallback(
    (invoice) => withUnmaskedBillToEmail(invoice, fetchClientForEmail),
    [fetchClientForEmail],
  );

  const componentRef = useRef();
  const [IC_DATA, setIC_DATA] = useState([]);

  const submitValidateRef = React.createRef();
  const cancelValidateRef = React.createRef();

  const shouldOpenFormFromLocationState = (state) =>
    Boolean(
      state?.mode &&
      !state?.view &&
      !state?.listOnly &&
      (state?.fromClientDetails || state?.data || state?.client_id != null),
    );

  const [showFormView, setShowFormView] = useState(() => {
    if (invoiceListFilters.listOnly) return false;
    return shouldOpenFormFromLocationState(location.state);
  });

  // Update clientDetails when location.state changes (for navigation from client details edit)
  useEffect(() => {
    if (location.state) {
      const category = location.state.category || location.state.data?.category || "individual";
      setClientDetails({
        ...location.state,
        category,
      });
      if (location.state.filterStatus) {
        setPaymentStatusFilter(location.state.filterStatus);
      } else if (invoiceListFilters.filterStatus) {
        setPaymentStatusFilter(invoiceListFilters.filterStatus);
      } else {
        setPaymentStatusFilter("All");
      }
    } else if (invoiceListFilters.filterStatus) {
      setPaymentStatusFilter(invoiceListFilters.filterStatus);
    } else {
      setClientDetails(null);
      setPaymentStatusFilter("All");
    }
  }, [location.state, invoiceListFilters.filterStatus]);

  useEffect(() => {
    if (invoiceListFilters.listOnly) {
      setShowFormView(false);
      return;
    }
    setShowFormView(shouldOpenFormFromLocationState(location.state));
  }, [location.state, invoiceListFilters.listOnly]);

  const [loading, setLoading] = useState(false);
  const [markPaidModal, setMarkPaidModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(() => {
    return (
      location.state?.filterStatus ||
      invoiceListFilters.filterStatus ||
      "All"
    );
  });
  const invoiceTableHeader = useMemo(
    () => [
      { id: "icon", label: "" },
      { id: "contract_no", label: "Invoice No." },
      { id: "contract_date", label: "Invoice Date" },
      { id: "billed_by", label: "Billed By" },
      { id: "billed_to", label: "Billed To" },
      { id: "total", label: "Net Total" },
      { id: "status", label: "Status" },
      { id: "payment_method", label: "Payment Method" },
      { id: "paid_date", label: "Paid Date" },
      { id: "updatedAt", label: "Last Updated" },
      { id: "action", label: "Action" },
    ],
    [],
  );
  const searchTimeoutRef = useRef(null);

  /** Employee/manager: assigned clients' invoices by default; search uses scope all. */
  const buildListFetchParams = (overrides = {}) => {
    const trimmed = String(
      overrides.search !== undefined ? overrides.search : searchQuery,
    ).trim();
    const base = { ...overrides };
    if (trimmed) base.search = trimmed;
    else delete base.search;
    const categoryToUse = overrides.category !== undefined ? overrides.category : clientDetails?.category;
    if (categoryToUse && base.billed_to) {
      base.category = normalizeClientDetailCategory(categoryToUse);
    }
    if (base.billed_to) {
      return buildAssignedClientScopeParams({ ...base, scope: "assigned" });
    }
    return buildAssignedClientScopeParams(base);
  };

  const reloadList = (overrides = {}) => {
    const next = { ...overrides };
    if (invoiceListFilters.startDate && next.start_date === undefined) {
      next.start_date = invoiceListFilters.startDate;
    }
    if (invoiceListFilters.endDate && next.end_date === undefined) {
      next.end_date = invoiceListFilters.endDate;
    }
    dispatch(MAIN_JSON.getAPI(buildListFetchParams(next)));
  };
  const markPaidRef = useRef();
  const undoPaidRef = useRef(null);

  // const [MAIN_JSON, setMAIN_JSON] = useState(
  //   clientDetails?.mode === "invoice" ? INVOICE : CONTRACT
  // );
  // const [MAIN_JSON, setMAIN_JSON] = useState(INVOICE);
  const [MAIN_JSON, setMAIN_JSON] = useState(() => {
    if (location?.state?.mode === "invoice") return INVOICE;
    if (location?.state?.mode === "contract") return CONTRACT;
    return INVOICE; // default
  });

  const [paymentStatusModal, setPaymentStatusModal] = useState(false);
  const [showInvoicePaymentPanel, setShowInvoicePaymentPanel] = useState(false);
  const [paymentPanelClient, setPaymentPanelClient] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    data: null,
  });

  const handlePaymentStatusOpen = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentStatusModal(true);
  };

  const handlePaymentStatusClose = () => {
    setPaymentStatusModal(false);
    setSelectedInvoice(null);
  };
  const defaultValues = (dateKey) => {
    return {
      [dateKey || MAIN_JSON.dateKey]: {
        value: dateFormatter(false, true),
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      due_date: {
        value: dateFormatter(false, true),
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      paid_date: {
        value: "",
        error: null,
        errmsg: null,
      },
      description: {
        value: "",
        validation: [{ name: "custommaxLength", params: "255" }],
        error: null,
        errmsg: null,
      },
      billed_by: {
        value: "",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      billed_to: {
        value: "",
        validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      discount_status: {
        value: false,
        // validation: [{ name: "required" }],
        error: null,
        errmsg: null,
      },
      discount_type: {
        value: "",
        error: null,
        errmsg: null,
      },
      discount: {
        value: "",
        error: null,
        errmsg: null,
      },
      discount_referral_status: {
        value: false,
        error: null,
        errmsg: null,
      },
      discount_referral_value: {
        value: "",
        error: null,
        errmsg: null,
      },
      organisation: {
        value: "",
        error: null,
        errmsg: null,
      },
      invoice_comment: {
        value: "",
        error: null,
        errmsg: null,
      },

      sub_total: {
        value: "",
      },
      total: {
        value: "",
      },
      itemDetails: [
        {
          item_name: "Individual Tax Service",
          description: "",
          description_is_custom: false,
          quantity: "",
          rate: "",
          amount: 0,
        },
      ],
      /// billed from details
      bill_from_first_name: {
        value: "",
      },
      bill_from_last_name: {
        value: "",
      },
      bill_from_email: {
        value: "",
      },
      bill_from_address_1: {
        value: "",
      },
      bill_from_address_2: {
        value: "",
      },
      bill_from_phone: {
        value: "",
      },
      bill_from_country: {
        value: "",
      },
      bill_from_state: {
        value: "",
      },
      bill_from_city: {
        value: "",
      },
      bill_from_zipcode: {
        value: "",
      },
      /// billed to details
      bill_to_first_name: {
        value: "",
      },
      bill_to_last_name: {
        value: "",
      },
      bill_to_email: {
        value: "",
      },
      bill_to_business_name: {
        value: "",
      },
      bill_to_business_type: {
        value: "",
      },
      bill_to_address_1: {
        value: "",
      },
      bill_to_address_2: {
        value: "",
      },
      bill_to_phone: {
        value: "",
      },
      bill_to_country: {
        value: "",
      },
      bill_to_state: {
        value: "",
      },
      bill_to_city: {
        value: "",
      },
      bill_to_zipcode: {
        value: "",
      },
    };
  };

  const editModeDefault = {
    mode: false,
    [MAIN_JSON.primaryID]: 0,
    currentData: [],
    currentDataItemRemoved: [],
  };

  const {
    contract: { allContracts },
    employee: { activeEmployees },
    client: { activeClients, allClients },
    invoice: { allInvoices },
    master: { payment_mode },
  } = useSelector((state) => state);

  //console.log(allClients, "allClients");
  const initialDataLoad = () => {
    if (clientDetails?.mode && clientDetails.data) {
      let clientData = detailsBind(
        billed_to_keys,
        [
          ...(clientDetails.data.client_type === "Business"
            ? billed_to_business_values
            : billed_to_personal_values),
        ],
        clientDetails.data,
      );
      const defaults = defaultValues(
        clientDetails?.mode === "invoice" && INVOICE.dateKey,
      );
      const loggedInUserId = localStorage.getItem("user_id");
      return {
        ...defaults,
        ...clientData,
        billed_by: {
          ...defaults.billed_by,
          value: lockBilledPartySelection ? loggedInUserId || "" : "",
        },
        billed_to: {
          ...defaults.billed_to,
          value: clientDetails.data.client_id || clientDetails.client_id || "",
        },
      };
    } else {
      return defaultValues();
    }
  };

  const [fieldConfiguration, setFieldConfiguration] =
    React.useState(initialDataLoad());

  // If Bill To was filled from a masked list email, resolve the real address.
  useEffect(() => {
    const email = fieldConfiguration?.bill_to_email?.value;
    const clientId =
      fieldConfiguration?.billed_to?.value ||
      clientDetails?.client_id ||
      clientDetails?.data?.client_id;
    if (!isMaskedEmail(email) || !clientId) return undefined;
    let cancelled = false;
    (async () => {
      const unmasked = await unmaskBillToEmail({
        bill_to_email: email,
        billed_to: clientId,
        client_id: clientId,
      });
      if (cancelled) return;
      if (unmasked?.bill_to_email && !isMaskedEmail(unmasked.bill_to_email)) {
        setFieldConfiguration((prev) => {
          if (!isMaskedEmail(prev?.bill_to_email?.value)) return prev;
          return {
            ...prev,
            bill_to_email: {
              ...prev.bill_to_email,
              value: unmasked.bill_to_email,
            },
          };
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    fieldConfiguration?.bill_to_email?.value,
    fieldConfiguration?.billed_to?.value,
    clientDetails,
    unmaskBillToEmail,
  ]);

  const isBusinessClient = useMemo(() => {
    const clientId = fieldConfiguration?.billed_to?.value || clientDetails?.client_id || clientDetails?.data?.client_id;
    if (!clientId) return false;
    const selectedClient = (activeClients || []).find(
      (c) => String(c.client_id) === String(clientId),
    );
    const clientType = selectedClient?.client_type || clientDetails?.data?.client_type;
    return String(clientType || "").trim().toLowerCase() === "business";
  }, [fieldConfiguration?.billed_to?.value, clientDetails, activeClients]);

  useEffect(() => {
    if (!lockBilledPartySelection) return;
    if (!activeEmployees?.length) return;

    const currentBilledBy =
      fieldConfiguration?.billed_by?.value || localStorage.getItem("user_id");

    if (!currentBilledBy) return;

    const selectedEmployee = activeEmployees.find(
      (employee) =>
        String(employee.employee_id) === String(currentBilledBy) ||
        String(employee.user_id) === String(currentBilledBy),
    );

    if (!selectedEmployee) return;

    const hasBilledByDetails =
      fieldConfiguration?.bill_from_first_name?.value ||
      fieldConfiguration?.bill_from_email?.value;

    if (
      hasBilledByDetails &&
      String(fieldConfiguration?.billed_by?.value) ===
        String(selectedEmployee.employee_id)
    ) {
      return;
    }

    const billedFromDetails = detailsBind(
      billed_from_keys,
      billed_from_values,
      selectedEmployee,
    );

    setFieldConfiguration((prevState) => ({
      ...prevState,
      ...billedFromDetails,
      billed_by: {
        ...prevState.billed_by,
        value: selectedEmployee.employee_id,
        error: null,
        errmsg: null,
      },
    }));
  }, [
    lockBilledPartySelection,
    activeEmployees,
    fieldConfiguration?.billed_by?.value,
    fieldConfiguration?.bill_from_first_name?.value,
    fieldConfiguration?.bill_from_email?.value,
  ]);
  const [documentContent, setDocumentContent] = useState({
    state: false,
    data: {},
  });

  useLayoutEffect(() => {
    dispatch(EMPLOYEE_ACTION.getActiveEmployees());
    dispatch(CLIENT_ACTION.getActiveClients());
    const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
    if (userRole === "employee" || userRole === "manager") {
      dispatch(CLIENT_ACTION.getAllClients({ scope: "assigned" }));
    }
    if (clientDetails?.mode) {
      const SELECTED_MODE =
        clientDetails.mode === "invoice" ? INVOICE : CONTRACT;
      setMAIN_JSON(SELECTED_MODE);
      const clientId =
        clientDetails.client_id || clientDetails.data?.client_id;
      if (clientId) {
        dispatch(
          SELECTED_MODE.getAPI(
            buildListFetchParams({ billed_to: clientId }),
          ),
        );
      } else {
        dispatch(SELECTED_MODE.getAPI(buildListFetchParams({})));
      }
    } else {
      const fetchParams = {};
      if (invoiceListFilters.startDate) {
        fetchParams.start_date = invoiceListFilters.startDate;
      }
      if (invoiceListFilters.endDate) {
        fetchParams.end_date = invoiceListFilters.endDate;
      }
      dispatch(INVOICE.getAPI(buildListFetchParams(fetchParams)));
    }
  }, []);

  const onAction = async (type, actionData, actionIndex) => {
    if (type === "done") {
      setMarkPaidModal(true);
      markPaidRef.current = { data: IC_DATA[actionIndex] };
      return;
    } else if (type === "paymentStatus") {
      const row = IC_DATA[actionIndex];
      handlePaymentStatusOpen(row?._invoiceRaw || row);
    } else if (type === "print" || type === "email" || type === "eye") {
      const row = IC_DATA[actionIndex];
      const invoiceId = row[MAIN_JSON.primaryID];

      const buildDocumentData = (fetched) => {
        const raw = row._invoiceRaw || row;
        const base = fetched || raw;
        return {
          ...base,
          status: base.status ?? raw.status,
          _invoiceRaw: raw._invoiceRaw || raw,
        };
      };

      const setContentAndProceed = async (fetched) => {
        const docData = await unmaskBillToEmail(buildDocumentData(fetched));
        setDocumentContent({
          state: type === "eye",
          data: docData,
        });
        await wait(200);
        await new Promise((resolve) =>
          requestAnimationFrame(() => requestAnimationFrame(resolve)),
        );
        if (type === "print") handlePrint();
        if (type === "email") {
          Loader(true);
          await handleSendMail(docData);
        }
      };

      if (MAIN_JSON.label === "Invoice" && invoiceId) {
        try {
          const response = await INVOICE_ACTION.getInvoiceDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            await setContentAndProceed(response.data.data);
          } else {
            await setContentAndProceed(row);
          }
        } catch (err) {
          console.error("Error fetching invoice details for PDF:", err);
          await setContentAndProceed(row);
        }
      } else if (MAIN_JSON.label === "Contract" && invoiceId) {
        try {
          const response = await CONTRACT_ACTION.getContractDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            await setContentAndProceed(response.data.data);
          } else {
            await setContentAndProceed(row);
          }
        } catch (err) {
          console.error("Error fetching contract details for PDF:", err);
          await setContentAndProceed(row);
        }
      } else {
        await setContentAndProceed(row);
      }
      return;
    } else if (["undo", "close"].includes(type)) {
      undoPaidRef.current = { data: IC_DATA[actionIndex], type };
      setMarkPaidModal(true);
      return;
    } else if (type === "pay" && MAIN_JSON.label === "Invoice") {
      const invoice = IC_DATA[actionIndex];
      const clientId =
        invoice?.billed_to ?? invoice?.bill_to ?? invoice?.client_id;
      if (!clientId) {
        Toaster.warning("Could not determine client for this invoice.");
        return;
      }
      const matchedClient = (activeClients || []).find(
        (c) => String(c.client_id ?? c.id) === String(clientId),
      );
      setPaymentPanelClient(
        matchedClient || {
          client_id: clientId,
          first_name: invoice.bill_to_first_name || "",
          last_name: invoice.bill_to_last_name || "",
          email: invoice.bill_to_email || "",
        },
      );
      setShowInvoicePaymentPanel(true);
      return;
    }
    const selectedData = IC_DATA[actionIndex];

    if (type === "edit") {
      if (MAIN_JSON.label === "Invoice" && isInvoiceCancelled(selectedData)) {
        Toaster.warning("Cancelled invoices cannot be edited.");
        return;
      }

      // onclick on edit smooth scroll
      customRef.current?.scrollIntoView({ behavior: "smooth" });
      setShowFormView(true);

      try {
        // Fetch full invoice/contract details to get complete itemDetails
        const invoiceId = selectedData[MAIN_JSON.primaryID];
        let fullData = selectedData;

        if (MAIN_JSON.label === "Invoice") {
          const response = await INVOICE_ACTION.getInvoiceDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            fullData = response.data.data;
          }
          if (isInvoiceCancelled(fullData)) {
            Toaster.warning("Cancelled invoices cannot be edited.");
            setShowFormView(false);
            return;
          }
        } else {
          const response = await CONTRACT_ACTION.getContractDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            fullData = response.data.data;
          }
        }

        const { itemDetails } = fullData;

        // Map itemDetails to match the expected structure
        const itemMapDetails = (itemDetails ?? []).map(
          (
            { item_name, quantity, description, rate, amount, status },
            index,
          ) => ({
            item_name: item_name || "",
            description: description || "",
            description_is_custom:
              Boolean(description) &&
              ![
                "2025 Federal Tax Filing",
                "2025 State Tax Filing",
                "Fbar & FATCA",
              ].includes(String(description)),
            quantity: quantity ? String(quantity) : "",
            rate: rate ? String(rate) : "",
            amount: amount || 0,
            status: status !== undefined ? status : 1,
            [MAIN_JSON.subID]: itemDetails[index]?.[MAIN_JSON.subID] || "",
          }),
        );

        fieldConfiguration[MAIN_JSON.dateKey].value =
          fullData[MAIN_JSON.dateKey];

        let details = {};

        editKeys.forEach((data) => {
          let nextValue;
          if (data === "discount_status") {
            nextValue = fullData[data] === 1;
          } else if (data === "discount_referral_status") {
            const raw = fullData.discount_referral_value;
            nextValue =
              raw !== undefined &&
              raw !== null &&
              String(raw).trim() !== "" &&
              String(raw).trim() !== "0";
          } else if (data === "discount_referral_value") {
            const raw = fullData[data];
            const allowed = DISCOUNT_REFERRAL_OPTIONS.map((o) => o.id);
            const str = raw === undefined || raw === null ? "" : String(raw);
            nextValue = allowed.includes(str) ? str : "";
          } else if (data === "organisation") {
            nextValue = fullData[data] || "";
          } else if (data === "paid_date") {
            nextValue = shouldShowInvoicePaidDate(fullData)
              ? paidDateToApiValue(getInvoicePaidDateRaw(fullData))
              : "";
          } else {
            nextValue = fullData[data] === 0 || fullData[data] || "";
          }
          details[data] = {
            ...fieldConfiguration[data],
            value: nextValue,
          };
        });

        if (isMaskedEmail(details.bill_to_email?.value)) {
          const unmasked = await unmaskBillToEmail({
            ...fullData,
            bill_to_email: details.bill_to_email.value,
          });
          if (unmasked?.bill_to_email && !isMaskedEmail(unmasked.bill_to_email)) {
            details.bill_to_email = {
              ...details.bill_to_email,
              value: unmasked.bill_to_email,
            };
          }
        }

        setClientDetails((prev) => {
          const next = prev ? { ...prev } : {};
          next.category = fullData.category || "individual";
          return next;
        });

        setEditMode({
          mode: true,
          [MAIN_JSON.primaryID]: fullData[MAIN_JSON.primaryID],
          currentData: fullData,
          currentDataItemRemoved: [],
        });

        setFieldConfiguration((prev) => ({
          ...prev,
          ...details,
          itemDetails:
            itemMapDetails.length > 0 ? itemMapDetails : prev.itemDetails,
        }));
      } catch (error) {
        console.error("Error loading invoice/contract details:", error);
        Toaster.error("Error loading invoice/contract details");
      }
    } else if (type == "delete") {
      setDeleteDialog({
        open: true,
        data: selectedData,
      });
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, data: null });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.data) return;
    try {
      const Id = deleteDialog.data[MAIN_JSON.primaryID];
      if (MAIN_JSON.label === "Invoice") {
        const response = await INVOICE.deleteAPI(Id);
        reloadList();
        const isSuccess =
          response?.status === 200 && response?.data?.status !== 0;
        if (isSuccess) {
          Toaster.success("Invoice is successfully deleted.");
        } else {
          Toaster.error("Error Deleting the Invoice.");
        }
      } else {
        const response = await CONTRACT.deleteAPI(Id);
        reloadList();
        const isSuccess =
          response?.status === 200 && response?.data?.status !== 0;
        if (isSuccess) {
          Toaster.success("Contract is successfully deleted.");
        } else {
          Toaster.error("Error Deleting the Contract.");
        }
      }
    } catch (error) {
      Toaster.error(`Error occurred: ${error.message}`);
    } finally {
      closeDeleteDialog();
    }
  };

  // Function to populate form when editing from client details
  const handleEditPopulation = async () => {
    try {
      const SELECTED_MODE =
        clientDetails.mode === "invoice" ? INVOICE : CONTRACT;
      const invoiceId = clientDetails.data[SELECTED_MODE.primaryID];
      let fullData = clientDetails.data;

      // Fetch full details if we have an ID
      if (invoiceId) {
        if (SELECTED_MODE.label === "Invoice") {
          const response = await INVOICE_ACTION.getInvoiceDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            fullData = response.data.data;
          }
        } else {
          const response = await CONTRACT_ACTION.getContractDetail(invoiceId);
          if (
            response &&
            response.status === 200 &&
            response.data &&
            response.data.data
          ) {
            fullData = response.data.data;
          }
        }
      }

      if (SELECTED_MODE.label === "Invoice" && isInvoiceCancelled(fullData)) {
        Toaster.warning("Cancelled invoices cannot be edited.");
        setShowFormView(false);
        return;
      }

      const { itemDetails } = fullData;

      // Map itemDetails to match the expected structure
      const itemMapDetails = (itemDetails ?? []).map(
        (
          { item_name, quantity, description, rate, amount, status },
          index,
        ) => ({
          item_name: item_name || "",
          description: description || "",
          description_is_custom:
            Boolean(description) &&
            ![
              "2025 Federal Tax Filing",
              "2025 State Tax Filing",
              "FBAR & FATCA",
            ].includes(String(description)),
          quantity: quantity ? String(quantity) : "",
          rate: rate ? String(rate) : "",
          amount: amount || 0,
          status: status !== undefined ? status : 1,
          [SELECTED_MODE.subID]:
            itemDetails[index]?.[SELECTED_MODE.subID] || "",
        }),
      );

      // Update date field
      const dateKeyToUse =
        SELECTED_MODE.label === "Invoice" ? INVOICE.dateKey : CONTRACT.dateKey;

      fieldConfiguration[dateKeyToUse].value = fullData[dateKeyToUse];

      let details = {};

      editKeys.forEach((data) => {
        let nextValue;
        if (data === "discount_status") {
          nextValue = fullData[data] === 1;
        } else if (data === "discount_referral_status") {
          const raw = fullData.discount_referral_value;
          nextValue =
            raw !== undefined &&
            raw !== null &&
            String(raw).trim() !== "" &&
            String(raw).trim() !== "0";
        } else if (data === "discount_referral_value") {
          const raw = fullData[data];
          const allowed = DISCOUNT_REFERRAL_OPTIONS.map((o) => o.id);
          const str = raw === undefined || raw === null ? "" : String(raw);
          nextValue = allowed.includes(str) ? str : "";
        } else if (data === "organisation") {
          nextValue = fullData[data] || "";
        } else if (data === "paid_date") {
          nextValue = shouldShowInvoicePaidDate(fullData)
            ? paidDateToApiValue(getInvoicePaidDateRaw(fullData))
            : "";
        } else {
          nextValue = fullData[data] === 0 || fullData[data] || "";
        }
        details[data] = {
          ...fieldConfiguration[data],
          value: nextValue,
        };
      });

      setClientDetails((prev) => {
        const next = prev ? { ...prev } : {};
        next.category = fullData.category || "individual";
        return next;
      });

      setEditMode({
        mode: true,
        [SELECTED_MODE.primaryID]: fullData[SELECTED_MODE.primaryID],
        currentData: fullData,
        currentDataItemRemoved: [],
      });

      setFieldConfiguration((prev) => ({
        ...prev,
        ...details,
        itemDetails:
          itemMapDetails.length > 0 ? itemMapDetails : prev.itemDetails,
      }));

      // Scroll to form
      setTimeout(() => {
        customRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } catch (error) {
      console.error("Error loading invoice/contract details for edit:", error);
      Toaster.error("Error loading invoice/contract details");
    }
  };

  useMemo(() => {
    if (clientDetails?.mode) {
      const SELECTED_MODE =
        clientDetails.mode === "invoice" ? INVOICE : CONTRACT;
      dispatch(
        SELECTED_MODE.getAPI(
          buildListFetchParams({ billed_to: clientDetails.client_id }),
        ),
      );
      setMAIN_JSON(SELECTED_MODE);
      if (clientDetails?.view) {
        setDocumentContent({
          state: true,
          data: clientDetails.data,
        });
      }
      // Handle edit mode - populate form with invoice data
      if (
        clientDetails?.data &&
        !clientDetails?.view &&
        clientDetails.data[SELECTED_MODE.primaryID]
      ) {
        handleEditPopulation();
      }
    } else {
      reloadList();
    }
  }, [location.state]);

  const [editMode, setEditMode] = useState(editModeDefault);

  const onItemAction = async (type, actionData, index) => {
    // for delete the row
    if (actionData[MAIN_JSON.subID]) {
      setEditMode((prev) => ({
        ...prev,
        currentDataItemRemoved: [
          ...prev.currentDataItemRemoved,
          actionData[MAIN_JSON.subID],
        ],
      }));
    }
    // if (index)
    fieldConfiguration.itemDetails.splice(index, 1);
    calculation();
  };

  const calculation = (discount_value, discount_type) => {
    let sub_total;

    if (!discount_value) {
      sub_total = fieldConfiguration.itemDetails.reduce(
        (accumulator, currentValue) =>
          accumulator + Number(currentValue.amount),
        0,
      );
      fieldConfiguration.sub_total.value = sub_total.toFixed(2);
    } else {
      sub_total = Number(fieldConfiguration.sub_total.value).toFixed(2);
    }

    // Apply regular discount whenever a value is entered (no checkbox).
    let discounted_total = sub_total;
    const effectiveDiscount =
      discount_value !== undefined && discount_value !== false
        ? discount_value
        : fieldConfiguration.discount.value;
    const effectiveType =
      discount_type || fieldConfiguration.discount_type.value;

    if (
      effectiveDiscount !== "" &&
      effectiveDiscount !== null &&
      effectiveDiscount !== undefined &&
      effectiveType
    ) {
      if (effectiveType === "Percentage") {
        discounted_total = (
          sub_total -
          (sub_total * Number(effectiveDiscount || 0)) / 100
        ).toFixed(2);
      } else if (effectiveType === "Amount") {
        discounted_total = (sub_total - Number(effectiveDiscount || 0)).toFixed(
          2,
        );
      }
    }

    if (discounted_total < 0) discounted_total = 0;

    fieldConfiguration.total.value = discounted_total;

    // Auto-derive boolean status flags from values (no checkboxes in UI).
    fieldConfiguration.discount_status.value =
      String(fieldConfiguration.discount.value || "").trim() !== "" &&
      String(fieldConfiguration.discount_type.value || "").trim() !== "";
    fieldConfiguration.discount_referral_status.value =
      String(fieldConfiguration.discount_referral_value.value || "").trim() !==
      "";

    setFieldConfiguration({ ...fieldConfiguration });
  };

  const onChange = (value, key, rowIndex) => {
    if (lockBilledPartySelection && key === "billed_to") {
      return;
    }

    if (key === "rate" || key === "quantity" || key === "discount")
      value = value.replace(/[^0-9.]/g, ""); // Ensure numeric input

    if (
      key === "discount" ||
      key === "discount_type" ||
      key === "discount_referral_value"
    ) {
      if (key === "discount_referral_value") {
        fieldConfiguration.discount_referral_value.value = value;
      } else if (key === "discount") {
        fieldConfiguration.discount.value = value;
      } else if (key === "discount_type") {
        fieldConfiguration.discount_type.value = value;
      }
      calculation(
        key === "discount" ? value : fieldConfiguration.discount.value,
        key === "discount_type"
          ? value
          : fieldConfiguration.discount_type.value,
      );
    }

    if (rowIndex || rowIndex === 0) {
      if (key === "description") {
        const isCustom = Boolean(
          fieldConfiguration.itemDetails[rowIndex]?.description_is_custom,
        );
        if (!isCustom && value === "Custom") {
          fieldConfiguration.itemDetails[rowIndex].description_is_custom = true;
          fieldConfiguration.itemDetails[rowIndex].description = "";
          setFieldConfiguration({ ...fieldConfiguration });
          return;
        }
        fieldConfiguration.itemDetails[rowIndex].description = value;
        setFieldConfiguration({ ...fieldConfiguration });
        return;
      }

      fieldConfiguration.itemDetails[rowIndex][key] = value;
      if (key === "rate" || key === "quantity") {
        fieldConfiguration.itemDetails[rowIndex].amount = (
          key === "quantity"
            ? fieldConfiguration.itemDetails[rowIndex].rate * value
            : fieldConfiguration.itemDetails[rowIndex].quantity * value
        ).toFixed(2);
        calculation();
      }
      setFieldConfiguration({ ...fieldConfiguration });
    } else {
      if (key === "billed_to" || key === "billed_by") {
        if (key === "billed_to")
          dispatch(
            MAIN_JSON.getAPI(
              buildListFetchParams({ billed_to: value, search: "" }),
            ),
          );

        const applyBilledParty = async () => {
          let selectedData = (
            key === "billed_to" ? activeClients : activeEmployees
          ).find((data) => {
            return (
              data[key === "billed_to" ? "client_id" : "employee_id"] === value
            );
          });

          if (
            key === "billed_to" &&
            selectedData &&
            isMaskedEmail(selectedData.email)
          ) {
            const detail = await fetchClientForEmail(value);
            if (detail?.email && !isMaskedEmail(detail.email)) {
              selectedData = { ...selectedData, ...detail, email: detail.email };
            }
          }

          if (!selectedData) return;

          let billed_details = detailsBind(
            key === "billed_to" ? billed_to_keys : billed_from_keys,
            key === "billed_to"
              ? [
                  ...(selectedData.client_type === "Business"
                    ? billed_to_business_values
                    : billed_to_personal_values),
                ]
              : billed_from_values,
            selectedData,
          );

          setFieldConfiguration((prevState) => ({
            ...prevState,
            ...billed_details,
          }));
        };

        applyBilledParty();
      }
      const errorcheck = ValidationLibrary.checkValidation(
        value,
        fieldConfiguration[key].validation,
      );
      const dynObj = {
        ...fieldConfiguration[key],
        value,
        error: !errorcheck.state,
        errmsg: errorcheck.msg,
      };

      setFieldConfiguration((prevState) => ({
        ...prevState,
        [key]: dynObj,
      }));
    }
  };

  const InvoiceStatus = {
    0: "Pending",
    1: "Paid",
    2: "Partially Paid",
    3: "Closed",
    4: "Cancelled",
    null: "Pending",
  };

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      reloadList({ search: searchQuery });
    }, 1000);

    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchQuery]);

  const contractTableData = useMemo(() => {
    const RESPONSE = [...allContracts].filter(
      (contract) =>
        contract.bill_to_first_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        contract.bill_to_last_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase()) ||
        contract.bill_to_email
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
    );

    const sortedData = RESPONSE.sort((a, b) =>
      a.updatedAt < b.updatedAt ? 1 : b.updatedAt < a.updatedAt ? -1 : 0,
    );

    setIC_DATA(sortedData);
    return sortedData.map((data) => {
      const {
        contract_no,
        contract_date,
        amount_paid = 0, // Ensure default value is 0
        bill_from_first_name,
        bill_from_last_name,
        bill_to_first_name,
        bill_to_last_name,
        sub_total,
        total, // Net Total
        status,
        updatedAt,
      } = data;

      const amountDue = Math.max(total - amount_paid, 0).toFixed(2);

      return {
        contract_no,
        contract_date: dateFormatter(contract_date),
        bill_from_name: `${bill_from_first_name} ${bill_from_last_name}`,
        bill_to_name: `${bill_to_first_name} ${bill_to_last_name}`,
        sub_total: `$ ${sub_total}`,
        total: `$ ${total}`, // Net Total
        amount_due: `$ ${amountDue}`,
        status:
          !status || status === 0
            ? "Pending"
            : status === 1
              ? "Proceeded to invoice"
              : "Inactive",
        updatedAt: dateTimeFormatter(updatedAt),
        enabledActions:
          !status || status === 0
            ? { print: true, email: true, edit: true, delete: true }
            : { print: true, email: true },
      };
    });
  }, [allContracts, searchQuery]);

  const invoiceTableData = useMemo(() => {
    const getComputedPaymentStatus = (invoice) =>
      getInvoicePaymentStatusLabel(invoice);

    const q = (searchQuery || "").toLowerCase();
    const numericQ = String(searchQuery || "").replace(/[$,\s]/g, "");
    const isNumericQuery = numericQ !== "" && !Number.isNaN(Number(numericQ));

    const invoiceRows = invoiceListFilters.billedByOnly
      ? [
          ...new Map(
            [...allInvoices].map((invoice) => [
              String(invoice.client_invoice_id || ""),
              invoice,
            ]),
          ).values(),
        ]
      : [...allInvoices];

    const RESPONSE = invoiceRows.filter((invoice) => {
      const totalNum = parseFloat(invoice?.total || 0);
      const subTotalNum = parseFloat(invoice?.sub_total || 0);
      const amountPaidNum = parseFloat(invoice?.amount_paid || 0);
      const amountDueNum = Math.max(totalNum - amountPaidNum, 0);

      const matchesAmount =
        isNumericQuery &&
        [totalNum, subTotalNum, amountPaidNum, amountDueNum].some((n) =>
          String(n).includes(numericQ),
        );

      const matchesSearch =
        !q ||
        invoice.bill_to_first_name?.toLowerCase().includes(q) ||
        invoice.bill_to_last_name?.toLowerCase().includes(q) ||
        invoice.bill_to_email?.toLowerCase().includes(q) ||
        invoice.bill_from_first_name?.toLowerCase().includes(q) ||
        invoice.bill_from_last_name?.toLowerCase().includes(q) ||
        invoice.bill_from_email?.toLowerCase().includes(q) ||
        invoice.invoice_no?.toLowerCase?.().includes(q) ||
        matchesAmount;

      if (!matchesSearch) return false;
      if (invoiceListFilters.billedByOnly) {
        const billedBy =
          invoice?.billed_by != null ? String(invoice.billed_by) : "";
        if (
          !invoiceListFilters.employeeIds.length ||
          !invoiceListFilters.employeeIds.includes(billedBy)
        ) {
          return false;
        }
      } else if (
        !invoiceMatchesEmployeeIds(invoice, invoiceListFilters.employeeIds)
      ) {
        return false;
      }
      if (
        invoiceListFilters.rawStatus != null &&
        invoiceListFilters.rawStatus !== ""
      ) {
        return Number(invoice?.status) === Number(invoiceListFilters.rawStatus);
      }
      if (paymentStatusFilter === "All") return true;

      return getComputedPaymentStatus(invoice) === paymentStatusFilter;
    });

    const sortedData = RESPONSE.sort((a, b) => {
      if (invoiceListFilters.sortOrder === "asc") {
        const dateA = new Date(a.invoice_date || 0).getTime();
        const dateB = new Date(b.invoice_date || 0).getTime();
        if (dateA !== dateB) return dateA - dateB;
        return String(a.invoice_no || "").localeCompare(
          String(b.invoice_no || ""),
          undefined,
          { numeric: true, sensitivity: "base" },
        );
      }

      return a.updatedAt < b.updatedAt ? 1 : b.updatedAt < a.updatedAt ? -1 : 0;
    });

    setIC_DATA(sortedData);
    return sortedData.map((data) => {
      const {
        invoice_no,
        invoice_date,
        bill_from_first_name,
        bill_from_last_name,
        bill_to_first_name,
        bill_to_last_name,
        total, // Net Total
        updatedAt,
      } = data;

      const computedStatus = getInvoicePaymentStatusLabel(data);
      const canPay = ["Pending", "Partially Paid"].includes(computedStatus);
      const isCancelled = isInvoiceCancelled(data);

      // Match client details: same actions for Pending and Partially Paid (not raw status === 0).
      const enabledActions = {
        print: true,
        email: true,
        paymentStatus: true,
        edit: !isCancelled,
        delete: true,
        pay: canPay,
      };

      return {
        contract_no: invoice_no,
        contract_date: dateFormatter(invoice_date),
        billed_by: `${bill_from_first_name} ${bill_from_last_name}`,
        billed_to: `${bill_to_first_name} ${bill_to_last_name}`,
        total: `$ ${total}`,
        status: computedStatus,
        payment_method: getInvoicePaymentMethodLabel(data),
        paid_date: shouldShowInvoicePaidDate(data)
          ? formatInvoicePaidDateDisplay(data)
          : "—",
        updatedAt: dateFormatter(updatedAt),
        enabledActions,
        _invoiceRaw: data,
      };
    });
  }, [
    allInvoices,
    searchQuery,
    paymentStatusFilter,
    invoiceListFilters.employeeIds,
    invoiceListFilters.sortOrder,
    invoiceListFilters.rawStatus,
    invoiceListFilters.billedByOnly,
  ]);

  const employeesData = useMemo(() => {
    //console.log("the active employees are", activeEmployees)
    return activeEmployees.map((data) => {
      const { employee_id } = data;
      return {
        id: employee_id,
        value: dropdownName(data),
      };
    });
  }, [activeEmployees]);

  const clientsData = useMemo(() => {
    //console.log("the active employees are", activeClients)

    return activeClients.map((data) => {
      const { client_id, email } = data;
      return {
        id: client_id,
        value: dropdownName(data) + " - " + email,
      };
    });
  }, [activeClients]);

  const lockedBilledByDisplay = useMemo(() => {
    const selected = employeesData.find(
      (item) =>
        String(item.id) === String(fieldConfiguration?.billed_by?.value),
    );
    if (selected?.value) return selected.value;

    const first = fieldConfiguration?.bill_from_first_name?.value || "";
    const last = fieldConfiguration?.bill_from_last_name?.value || "";
    const fullName = `${first} ${last}`.trim();
    if (fullName) return fullName;

    const localFirst = localStorage.getItem("first_name") || "";
    const localLast = localStorage.getItem("last_name") || "";
    const localName = `${localFirst} ${localLast}`.trim();
    if (localName) return localName;

    return fieldConfiguration?.bill_from_email?.value || "";
  }, [
    employeesData,
    fieldConfiguration?.billed_by?.value,
    fieldConfiguration?.bill_from_first_name?.value,
    fieldConfiguration?.bill_from_last_name?.value,
    fieldConfiguration?.bill_from_email?.value,
  ]);

  const lockedBilledToDisplay = useMemo(() => {
    const selected = clientsData.find(
      (item) =>
        String(item.id) === String(fieldConfiguration?.billed_to?.value),
    );
    if (selected?.value) return selected.value;

    const first = fieldConfiguration?.bill_to_first_name?.value || "";
    const last = fieldConfiguration?.bill_to_last_name?.value || "";
    const fullName = `${first} ${last}`.trim();
    if (fullName) return fullName;

    const clientData = clientDetails?.data || {};
    const clientFallbackName = [
      clientData.business_name,
      clientData.company_name,
      clientData.client_name,
      clientData.name,
      `${clientData.first_name || ""} ${clientData.last_name || ""}`.trim(),
    ].find((item) => item && String(item).trim());
    if (clientFallbackName) return clientFallbackName;

    return fieldConfiguration?.bill_to_email?.value || "";
  }, [
    clientsData,
    clientDetails?.data,
    fieldConfiguration?.billed_to?.value,
    fieldConfiguration?.bill_to_first_name?.value,
    fieldConfiguration?.bill_to_last_name?.value,
    fieldConfiguration?.bill_to_email?.value,
  ]);

  const defaultFields = [
    {
      type: "select",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
      dropdown: INVOICE_LINE_ITEM_NAME_OPTIONS,
      getDropdown: (rowIndex, rows) => {
        const opts = [...INVOICE_LINE_ITEM_NAME_OPTIONS];
        const raw = rows?.[rowIndex]?.item_name;
        const name =
          typeof raw === "string" ? raw.trim() : raw != null ? String(raw) : "";
        if (
          name &&
          !opts.some((o) => String(o.value) === name || String(o.id) === name)
        ) {
          opts.push({ id: name, value: name });
        }
        return opts;
      },
      changeData: onChange,
    },
    {
      type: "select",
      fieldKey: "description",
      validation: [{ name: "custommaxLength", params: "255" }],
      error: null,
      errmsg: null,
      changeData: onChange,
      dropdown: [
        { id: "2025 Federal Tax Filing", value: "2025 Federal Tax Filing" },
        { id: "2025 State Tax Filing", value: "2025 State Tax Filing" },
        { id: "Fbar & FATCA", value: "Fbar & FATCA" },
        { id: "Custom", value: "Custom" },
      ],
      getFieldType: (rowIndex, rows) =>
        rows?.[rowIndex]?.description_is_custom ? "text" : "select",
    },
    {
      type: "text",
      validation: [
        { name: "required" },
        { name: "custommaxLength", params: "3" },
      ],
      error: null,
      errmsg: null,
      changeData: onChange,
    },
    {
      type: "text",
      validation: [
        { name: "required" },
        { name: "custommaxValue", params: "99999" },
      ],
      error: null,
      errmsg: null,
      changeData: onChange,
    },
    {
      type: "label",
    },

    {
      type: "label",
    },
    {
      type: "label",
    },
  ];

  const defaultMarkPaidFields = {
    payment_mode_id: {
      value: "",
      labelname: "Mode of payment",
      type: "select",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
      dropdown: payment_mode,
    },
    transaction_id: {
      value: "",
      labelname: "Transaction number",
      type: "text",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    reference_number: {
      value: "",
      labelname: "Reference number",
      type: "text",
      // validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    payment_date: {
      value: "",
      labelname: "Payment date",
      type: "datepicker",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    payment_amount_paid: {
      value: "",
      labelname: "Amount",
      type: "text",
      disabled: true,
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
    payment_comments: {
      value: "",
      labelname: "Comments",
      type: "textarea",
      maxRows: 1,
    },
  };

  const defaultUpdateStatusFields = {
    reason: {
      value: "",
      labelname: "Reason",
      type: "text",
      validation: [{ name: "required" }],
      error: null,
      errmsg: null,
    },
  };

  async function onSubmit(actionType) {
    try {
      if (["markAsPaid", "statusUpdation"].includes(actionType?.type)) {
        const response = await INVOICE_ACTION.invoicePaidUpdate(
          actionType.payload,
          actionType?.type,
        );
        if (response?.status === "Success") {
          if (actionType?.type === "markAsPaid") {
            const inv =
              actionType?.payload?.invoice ||
              actionType?.payload ||
              markPaidRef.current?.data ||
              null;
            try {
              await syncEfilingPaymentStatusFromInvoice({
                clientId:
                  inv?.billed_to ??
                  inv?.bill_to ??
                  inv?.client_id ??
                  actionType?.payload?.billed_to ??
                  null,
                invoiceNo:
                  inv?.invoice_no ??
                  inv?.invoiceNo ??
                  actionType?.payload?.invoice_no ??
                  null,
                clientInvoiceId:
                  getClientInvoiceId(inv) ||
                  actionType?.payload?.client_invoice_id ||
                  null,
                paymentStatus: "Paid",
              });
            } catch (_e) {
              /* best-effort */
            }
          }
          Toaster.success(
            `Payment ${
              actionType?.type === "markAsPaid" ? "updated" : "reverted"
            } successfully`,
          );
          reloadList();
          setMarkPaidModal(false);
          markPaidRef.current = null;
          undoPaidRef.current = null;
        } else {
          Toaster.error(`Process not successfully held`);
        }
        return;
      }

      if (
        editMode.mode &&
        MAIN_JSON.label === "Invoice" &&
        isInvoiceCancelled(editMode.currentData)
      ) {
        Toaster.warning("Cancelled invoices cannot be edited.");
        return;
      }

      const mainvalue = {};
      const targetkeys = Object.keys(fieldConfiguration);
      for (const i in targetkeys) {
        if (targetkeys[i] === "itemDetails") break;
        const errorcheck = ValidationLibrary.checkValidation(
          fieldConfiguration[targetkeys[i]].value,
          fieldConfiguration[targetkeys[i]].validation,
        );
        fieldConfiguration[targetkeys[i]].error = !errorcheck.state;
        fieldConfiguration[targetkeys[i]].errmsg = errorcheck.msg;
        mainvalue[targetkeys[i]] = fieldConfiguration[targetkeys[i]].value;
      }
      const filtererr = targetkeys.filter(
        (obj) => fieldConfiguration[obj].error === true,
      );

      if (
        new Date(fieldConfiguration.due_date.value) <
          new Date(fieldConfiguration[MAIN_JSON.dateKey]?.value) ||
        filtererr.length > 0
      ) {
        Toaster.error("Please fill up required fields");
      } else {
        const checkValidity = submitValidateRef.current;
        const filterError = checkValidity();
        if (filterError) {
          Toaster.error("Please fill up required fields");
        } else {
          Loader(true);
          let itemParams = [];

          if (editMode.currentDataItemRemoved.length > 0) {
            const selectedData = editMode?.currentData;
            itemParams = editMode.currentDataItemRemoved.map((_id) => {
              let { item_name, quantity, description, rate, amount } =
                selectedData.itemDetails.find(
                  (data) => data[MAIN_JSON.subID] === _id,
                );
              return {
                item_name,
                quantity,
                description,
                rate,
                amount,
                [MAIN_JSON.subID]: _id,
                status: 0,
              };
            });
            itemParams = [...fieldConfiguration.itemDetails, ...itemParams];
          } else {
            itemParams = fieldConfiguration.itemDetails;
          }

          const payload = {};
          const index = targetkeys.indexOf("itemDetails");
          if (index !== -1) {
            targetkeys.splice(index, 1);
          }

          targetkeys.forEach(
            (data) => (payload[data] = fieldConfiguration[data].value),
          );

          // Never persist / email a masked Bill To address from list APIs.
          if (isMaskedEmail(payload.bill_to_email)) {
            const unmasked = await unmaskBillToEmail({
              bill_to_email: payload.bill_to_email,
              billed_to: payload.billed_to,
              client_id: payload.billed_to,
            });
            if (
              unmasked?.bill_to_email &&
              !isMaskedEmail(unmasked.bill_to_email)
            ) {
              payload.bill_to_email = unmasked.bill_to_email;
              setFieldConfiguration((prev) => ({
                ...prev,
                bill_to_email: {
                  ...prev.bill_to_email,
                  value: unmasked.bill_to_email,
                },
              }));
            }
          }

          // Discount status is auto-derived from the entered value (no checkbox).
          const hasDiscount =
            String(fieldConfiguration.discount.value || "").trim() !== "" &&
            String(fieldConfiguration.discount_type.value || "").trim() !== "";
          payload["discount_status"] = hasDiscount ? 1 : 2;

          // Referral discount is now a category (Royalty / First Year Filing Discount / Referral Discount).
          const referralCategory = String(
            fieldConfiguration.discount_referral_value.value || "",
          ).trim();
          payload["discount_referral_value"] = referralCategory;
          payload["discount_referral_status"] = referralCategory ? 1 : 2;

          payload["organisation"] = String(
            fieldConfiguration.organisation.value || "",
          ).trim();

          if (isBusinessClient) {
            payload.category = normalizeClientDetailCategory(
              clientDetails?.category || "business",
            );
          } else {
            payload.category = "individual";
          }

          {
            const d =
              editMode.mode && editMode.currentData
                ? editMode.currentData
                : null;
            payload["amount_paid"] =
              d && d.amount_paid !== undefined && d.amount_paid !== null
                ? String(d.amount_paid)
                : "0";
            payload["payment_status"] =
              (d && (d.payment_status ?? d.paymentStatus)) || "Pending";
            payload["payment_method"] =
              (d && (d.payment_method ?? d.payment_mode)) || "none";
          }

          if (MAIN_JSON.label !== "Invoice") {
            delete payload.invoice_comment;
          }

          if (editMode.mode) {
            const payStatus =
              (editMode.currentData?.payment_status ??
                editMode.currentData?.paymentStatus) ||
              getInvoicePaymentStatusLabel(editMode.currentData);
            if (payStatus === "Paid" || payStatus === "Partially Paid") {
              payload.paid_date =
                paidDateToApiValue(fieldConfiguration.paid_date?.value) || "";
            } else {
              payload.paid_date = "";
            }
          } else {
            delete payload.paid_date;
          }

          // **Ensure `status` is set to 0 for new invoices or retain its value for updates**
          payload["status"] =
            editMode.mode && editMode.currentData.status !== undefined
              ? editMode.currentData.status
              : "0";

          let payloadData = {
            ...payload,
            itemDetails: JSON.stringify(itemParams),
          };

          let formData = new FormData();
          if (editMode.mode) {
            setDocumentContent({
              state: false,
              data: {
                ...payloadData,
                [MAIN_JSON.no]: editMode.currentData[MAIN_JSON.no],
                itemDetails: fieldConfiguration.itemDetails,
              },
            });

            await wait(2000);
            formData.append("file", await getDocumentBlob());
            formData.append(MAIN_JSON.no, editMode.currentData[MAIN_JSON.no]);
          }

          Object.entries(payloadData).forEach(([formKey, formValue]) => {
            formData.append(formKey, formValue);
          });

          const response = await MAIN_JSON.insertAPI({
            params: formData,
            insertUpdateFlag: editMode.mode ? 1 : 0,
            [MAIN_JSON.primaryID]: editMode[MAIN_JSON.primaryID],
          });

          if (response.status === 200 && response.data.status === 1) {
            if (editMode.mode === false) {
              setLoading(true);
              // For email PDF generation, prefer the form payload values (includes discount)
              // and merge in server fields (ids/numbers). Server response sometimes omits
              // discount fields which breaks the mailed PDF while preview/print is correct.
              const serverData = response.data.data || {};
              // Prefer form payload values for discount fields because the create API
              // response can omit/zero them; but keep server identifiers (invoice_no/id).
              const merged = {
                ...serverData,
                ...payloadData,
                itemDetails: fieldConfiguration.itemDetails,
                [MAIN_JSON.no]:
                  serverData[MAIN_JSON.no] || payloadData[MAIN_JSON.no],
                [MAIN_JSON.primaryID]:
                  serverData[MAIN_JSON.primaryID] ||
                  payloadData[MAIN_JSON.primaryID],
              };
              // ContractInvoiceView expects numeric discount_status (1=enabled).
              const emailDocData = {
                ...merged,
                discount_status:
                  typeof merged.discount_status === "boolean"
                    ? merged.discount_status
                      ? 1
                      : 2
                    : merged.discount_status,
              };
              setDocumentContent({
                state: false,
                data: emailDocData,
              });
              await wait(300);
              await handleSendMail(emailDocData, true);
            }

            Toaster.success(
              `${[MAIN_JSON.label]} ${
                !editMode.mode ? "created" : "updated"
              } successfully`,
            );
            handleCancel();
            setShowFormView(false);
          } else if (response.status === 200 && response.data.status === 0) {
            Toaster.warning(response.data.message);
          } else {
            Toaster.error(
              `Error occurred while ${
                !editMode.mode ? "creating" : "updating"
              } the ${MAIN_JSON.label}`,
            );
          }
        }
      }
      setFieldConfiguration((prevState) => ({
        ...prevState,
      }));
    } catch (err) {
      Toaster.error(err);
    }
  }

  function handleCancel() {
    const cancel = cancelValidateRef.current;
    cancel();
    setSearchQuery("");
    reloadList({ search: "" });
    setFieldConfiguration(defaultValues());
    editMode.mode && setEditMode(editModeDefault);
    setShowFormView(false);
    setClientDetails(null);
  }

  const modeChange = (action) => {
    const SELECTED_MODE = action ? INVOICE : CONTRACT;
    setMAIN_JSON(SELECTED_MODE);
    setFieldConfiguration(defaultValues(SELECTED_MODE.dateKey));
    editMode.mode && setEditMode(editModeDefault);
    setClientDetails();
    setSearchQuery("");
    dispatch(SELECTED_MODE.getAPI(buildListFetchParams({})));
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  async function handleSendMail(contentData, create) {
    // Ensure the hidden printable view is rendered with the latest data
    // before snapshotting it into a PDF blob.
    if (contentData && typeof contentData === "object") {
      setDocumentContent((prev) => ({
        ...prev,
        data: contentData,
      }));
      await wait(150);
      await new Promise((resolve) =>
        window.requestAnimationFrame(() =>
          window.requestAnimationFrame(resolve),
        ),
      );
    }

    let formData = new FormData();
    formData.append("file", await getDocumentBlob());

    const response = await CONTRACT_ACTION.sendEmail({
      binaryData: formData,
      create: create || false,
      origin: MAIN_JSON.label,
      id: contentData[MAIN_JSON.primaryID],
    });
    if (create) {
      setLoading(false);
    }
    if (response.status === 200 && response.data.status === 1) {
      Toaster.success(`Attachment has been successfully sent to the client `);
    } else if (response.status === 200 && response.data.status === 0) {
      Toaster.warning(response.data.message);
    } else {
      Toaster.error(`Error occurred while sending the attachment`);
    }
  }

  const View = ({ id }) => (
    <ContractInvoiceView
      inputRef={componentRef}
      id={id || false}
      data={documentContent.data}
      label={MAIN_JSON.label}
    />
  );

  const MarkAsPaid = () => {
    const undoMode = undoPaidRef.current;
    const [markPaidFields, setMarkPaidFields] = useState(
      undoMode ? defaultUpdateStatusFields : defaultMarkPaidFields,
    );
    const keys = Object.keys(markPaidFields);

    useEffect(() => {
      if (markPaidRef?.current) {
        setMarkPaidFields((prev) => ({
          ...prev,
          payment_amount_paid: {
            ...prev.payment_amount_paid,
            value: markPaidRef.current.data.total,
          },
        }));
      }
    }, [markPaidRef]);

    const handleSubmit = () => {
      const isValid = onSubmitValidation({
        state: markPaidFields,
        setState: setMarkPaidFields,
      });
      if (!isValid.length) {
        let payload = {};
        for (let key in markPaidFields) {
          payload[key] = markPaidFields[key].value;
        }
        onSubmit({
          type: undoMode ? "statusUpdation" : "markAsPaid",
          payload: undoMode
            ? {
                ...payload,
                invoice_id: undoMode.data.client_invoice_id,
                status: undoMode.type === "undo" ? 0 : 3, //status notation {undo:0,close:3}
              }
            : {
                ...payload,
                invoice_id: markPaidRef.current.data.client_invoice_id,
              },
        });
      }
    };

    return (
      <Grid
        container
        spacing={3}
        className={`contract-padding ${undoMode && "undo-content"}`}
      >
        {keys.map((data) => (
          <Grid item xs={12} sm={4} md={3} lg={3}>
            <Labelbox
              {...markPaidFields[data]}
              value={markPaidFields[data].value || ""}
              changeData={(value) =>
                checkValidation({
                  key: data,
                  value: value,
                  state: markPaidFields,
                  setState: setMarkPaidFields,
                })
              }
            />
          </Grid>
        ))}
        <Grid item xs={12}>
          <div className="hbox">
            <Button text={`Submit`} large handleClick={handleSubmit} />
            <Button
              text="Cancel"
              large
              handleClick={() => {
                cancelValidation({
                  setState: setMarkPaidFields,
                  defaultState: defaultMarkPaidFields,
                });
                setMarkPaidModal(false);
                markPaidRef.current = null;
                undoPaidRef.current = null;
              }}
            />
          </div>
        </Grid>
      </Grid>
    );
  };

  return (
    <div
      className=""
      style={{
        backgroundColor: "#f5f7fa",
        paddingLeft: "1.3rem",
        paddingRight: "1.3rem",
        paddingTop: "9px",
      }}
    >
      <Grid
        container
        alignItems="center"
        spacing={2}
        className="grid-padding "
        ref={customRef}
      >
        <div style={{ display: "none" }}>
          {Object.keys(documentContent.data).length > 0 && (
            <View id="divToPrint" />
          )}
        </div>

        {/* TABLE VIEW - Show by default when not in form view */}
        {!showFormView && (
          <Grid item xs={12} ref={divRef} className="invoice-page">
            <div className="page-header-row" style={{ marginBottom: "1.5rem" }}>
              {/* <div className="TitlePane" style={{ fontSize: "24px" }}>
                {MAIN_JSON.label}
              </div> */}
              {/* <div className="page-actions">
                <Button
                  className="timesheet-add-row-btn"
                  variant="outline"
                  icon={<AddCircleOutlineOutlined />}
                  text="Create Invoice"
                  large
                  handleClick={() => setShowFormView(true)}
                />
              </div> */}
            </div>

            <div className="timesheet-table-card panel">
              <div
                style={{
                  display: "flex",
                  justifyContent: "right",
                  alignItems: "center",
                  marginBottom: "1.2rem",
                }}
                className="invoice-table-header"
              >
                <div className="dash-search" style={{ marginRight: "12px" }}>
                  <SearchIcon className="dash-search-icon" />
                  <input
                    type="text"
                    placeholder="Search by Invoice No. / Billed To / Amount"
                    aria-label="Search by Invoice No. / Billed To / Amount"
                    onChange={(e) => setSearchQuery(e.target.value)}
                    value={searchQuery}
                  />
                </div>
                {MAIN_JSON.label === "Invoice" ? (
                  <Labelbox
                    type="select"
                    labelname=""
                    value={paymentStatusFilter}
                    changeData={(value) => setPaymentStatusFilter(value)}
                    dropdown={[
                      { id: "All", value: "All Payment Status" },
                      { id: "Paid", value: "Paid" },
                      { id: "Pending", value: "Pending" },
                      { id: "Partially Paid", value: "Partially Paid" },
                      { id: "Cancelled", value: "Cancelled" },
                    ]}
                  />
                ) : null}
              </div>

              <div className="client-details-invoices">
                <Table
                  aligncss="dashboard-table"
                  variant="small"
                  header={
                    MAIN_JSON.label === "Invoice"
                      ? invoiceTableHeader
                      : [
                          { id: "icon", label: "" },
                          { id: "contract_no", label: `${MAIN_JSON.label} No.` },
                          { id: "contract_date", label: `${MAIN_JSON.label} Date` },
                          { id: "billed_by", label: "Billed By" },
                          { id: "billed_to", label: "Billed To" },
                          { id: "sub_total", label: "Gross Total" },
                          { id: "total", label: "Net Total" },
                          { id: "amount_due", label: "Amount Due" },
                          { id: "status", label: "Status" },
                          { id: "updated", label: "Last Updated" },
                          { id: "action", label: "Action" },
                        ]
                  }
                  data={
                    MAIN_JSON.label === "Invoice"
                      ? invoiceTableData
                      : contractTableData
                  }
                  actions={
                    MAIN_JSON.label === "Invoice"
                      ? ["edit", "paymentStatus"]
                      : [
                          "print",
                          "email",
                          "edit",
                          "paymentStatus",
                          "delete",
                        ]
                  }
                  menuActions={
                    MAIN_JSON.label === "Invoice"
                      ? ["print", "email", "delete", "pay"]
                      : undefined
                  }
                  onActionClick={onAction}
                  restrictActionIcon={
                    MAIN_JSON.label === "Invoice"
                  }
                  icons={{ eyeIcon: true }}
                  hiddenFields={
                    MAIN_JSON.label === "Invoice"
                      ? ["enabledActions", "_invoiceRaw"]
                      : undefined
                  }
                />

                {MAIN_JSON.label === "Invoice" &&
                  showInvoicePaymentPanel &&
                  paymentPanelClient && (
                    <div
                      className="invoice-payment-panel"
                      style={{ marginTop: "16px" }}
                    >
                      <ContractPayment
                        userId={
                          paymentPanelClient.client_id ??
                          paymentPanelClient.id
                        }
                        USER_DATA={paymentPanelClient}
                        fetchAll={true}
                      />
                    </div>
                  )}
              </div>
            </div>
          </Grid>
        )}

        {/* FORM VIEW - Show only when showFormView is true */}
        {showFormView && (
          <>
            {/* ---------- Page Title row (header actions) ---------- */}
            <Grid item xs={12}>
              <div className="page-header-row">
                <div className="TitlePane" style={{ fontSize: "24px" }}>
                  {editMode.mode
                    ? `Edit ${MAIN_JSON.label}`
                    : `Create ${MAIN_JSON.label}`}
                </div>

                <div className="page-actions">
                  <Button
                    className="timesheet-add-row-btn"
                    variant="outline"
                    icon={<ArrowBack />}
                    text="Back"
                    large
                    handleClick={() => {
                      setShowFormView(false);
                      setFieldConfiguration(defaultValues());
                      editMode.mode && setEditMode(editModeDefault);
                      setClientDetails(null);
                    }}
                  />
                  {/* <button
                    className="employee-btn-cancel-2"
                    handleClick={handleCancel}
                  >
                    Reset
                  </button>
                  <Button
                    className="timesheet-add-row-btn"
                    variant="outline"
                    icon={editMode.mode ? <Edit /> : <AddCircleOutlineOutlined />}
                    text={editMode.mode ? "Update Invoice" : "Create Invoice"}
                    large
                    handleClick={onSubmit}
                  /> */}
                </div>
              </div>

              {/* ---------- Card container immediately below header ---------- */}
              <div className="card-create-invoice ">
                {/* <div className="card-create-invoice-title">
                  {editMode.mode ? "Edit Invoice" : "Create Invoice"}
                </div> */}

                <div className="card-create-invoice-body">
                  <div className="date-row" style={{ gridTemplateColumns: isBusinessClient ? "repeat(3, 1fr)" : "repeat(2, 1fr)" }}>
                    <div className="date-col title-field">
                      <Labelbox
                        type="datepicker"
                        labelname="Invoice Date"
                        changeData={(e) => onChange(e, MAIN_JSON.dateKey)}
                        value={fieldConfiguration[MAIN_JSON.dateKey]?.value}
                        error={fieldConfiguration[MAIN_JSON.dateKey]?.error}
                        errmsg={fieldConfiguration[MAIN_JSON.dateKey]?.errmsg}
                      />
                    </div>

                    <div className="date-col title-field">
                      <Labelbox
                        type="datepicker"
                        labelname="Due Date"
                        disablePast
                        minDate={fieldConfiguration[MAIN_JSON.dateKey]?.value}
                        changeData={(data) => onChange(data, "due_date")}
                        minDateMessage={`Date must be after ${MAIN_JSON.label?.toLowerCase()} date`}
                        value={fieldConfiguration.due_date.value}
                        error={
                          fieldConfiguration.due_date.error ||
                          new Date(fieldConfiguration.due_date.value) <
                            new Date(
                              fieldConfiguration[MAIN_JSON.dateKey]?.value,
                            )
                        }
                        errmsg={fieldConfiguration.due_date.errmsg}
                      />
                    </div>

                    {editMode.mode &&
                      shouldShowInvoicePaidDate(editMode.currentData) && (
                      <div className="date-col title-field">
                        <Labelbox
                          type="datepicker"
                          labelname="Paid Date"
                          changeData={(data) => onChange(data, "paid_date")}
                          value={fieldConfiguration.paid_date?.value}
                          error={fieldConfiguration.paid_date?.error}
                          errmsg={fieldConfiguration.paid_date?.errmsg}
                        />
                      </div>
                    )}

                    {isBusinessClient && (
                      <div className="date-col title-field">
                        <Labelbox
                          type="select"
                          labelname="Category"
                          dropdown={[
                            { id: "individual", value: "Individual" },
                            { id: "business", value: "Business" },
                          ]}
                          changeData={(val) => {
                            setClientDetails((prev) => {
                              const nextDetails = { ...prev, category: val };
                              const clientId = nextDetails?.client_id || nextDetails?.data?.client_id;
                              if (clientId) {
                                const params = buildListFetchParams({
                                  billed_to: clientId,
                                  category: val,
                                });
                                dispatch(MAIN_JSON.getAPI(params));
                              }
                              return nextDetails;
                            });
                          }}
                          value={clientDetails?.category || ""}
                        />
                      </div>
                    )}

                    {/* <div className="date-col title-field" style={{}}>
                      <Labelbox
                        // style={{ width: "100%",paddingTop:50 }}
                        // multiline={true}
                        type="text"
                        labelname="Invoice title/subject"
                        changeData={(e) => onChange(e, "description")}
                        value={fieldConfiguration.description.value}
                        error={fieldConfiguration.description.error}
                        errmsg={fieldConfiguration.description.errmsg}
                      />
                    </div> */}
                  </div>
                </div>
              </div>
            </Grid>

            <Grid
              className="paddingTop"
              style={{
                paddingLeft: 10,
                paddingRight: 10,
                marginBottom: 10,
                width: "100%",
              }}
            >
              {/* <Grid item xs={12} sm={6} md={6} lg={6} className="full-height">
                <div
                  className="card-create-invoice billed-by-select"
                  style={{ backgroundColor: "white" }}
                >
                  <span
                    className="card-create-invoice-title "
                    style={{ display: "block" }}
                  >
                    Billed By (Your Details)
                  </span>
                  {lockBilledPartySelection ? (
                    <TextField
                      disabled
                      fullWidth
                      variant="outlined"
                      value={lockedBilledByDisplay || "-"}
                      InputProps={{ readOnly: true }}
                      style={{ marginTop: 8 }}
                    />
                  ) : (
                    <Labelbox
                      type="select"
                      labelname=""
                      changeData={(e) => onChange(e, "billed_by")}
                      value={fieldConfiguration.billed_by.value}
                      required
                      dropdown={employeesData}
                      error={fieldConfiguration.billed_by.error}
                      errmsg={fieldConfiguration.billed_by.errmsg}
                    />
                  )}
                  <Paper className="no-toolbox-padding">
                    <InfoTable
                      info={{
                        name: "FinloTax Inc",
                        address: "1190 Miraloma Way Ste P, Sunnyvale, CA 94085",
                        email: "info@finlotax.com",
                      }}
                    />
                  </Paper>
                </div>
              </Grid> */}

              <div className="billing-two-column-row">
                <div className="billing-col-half">
                  <div
                    className="card-create-invoice billed-by-select"
                    style={{ backgroundColor: "white" }}
                  >
                    <span
                      className="card-create-invoice-title "
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      Billed By (Your Details)
                    </span>
                    <Labelbox
                      type="select"
                      labelname=""
                      changeData={(e) => onChange(e, "billed_by")}
                      value={fieldConfiguration.billed_by.value}
                      required
                      dropdown={employeesData}
                      error={fieldConfiguration.billed_by.error}
                      errmsg={fieldConfiguration.billed_by.errmsg}
                    />
                  </div>
                </div>

                <div className="billing-col-half">
                  <div
                    className="card-create-invoice billed-by-select"
                    style={{ backgroundColor: "white" }}
                  >
                    <span
                      className="card-create-invoice-title "
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      Billed To ({MAIN_JSON.label} Details)
                    </span>
                    {lockBilledPartySelection || editMode.mode ? (
                      <></>
                    ) : (
                      <Labelbox
                        type="select"
                        labelname=""
                        changeData={(e) => onChange(e, "billed_to")}
                        value={fieldConfiguration.billed_to.value}
                        required
                        dropdown={clientsData}
                        error={fieldConfiguration.billed_to.error}
                        errmsg={fieldConfiguration.billed_to.errmsg}
                      />
                    )}
                    <Paper>
                      <InfoTable
                        // titles={["Name", "Email", "Phone","Address","City","Country"]}
                        info={
                          fieldConfiguration.billed_to.value && {
                            name: `${
                              fieldConfiguration?.bill_to_first_name.value || ""
                            } ${fieldConfiguration?.bill_to_last_name.value || ""}`,
                            ...(activeClients.find(
                              (client) =>
                                String(client.client_id) ===
                                  String(fieldConfiguration.billed_to.value) &&
                                client.client_type === "Business",
                            )
                              ? {
                                  business_name:
                                    fieldConfiguration?.bill_to_business_name
                                      .value,
                                }
                              : {}),
                            email: fieldConfiguration?.bill_to_email.value,
                            phone:
                              fieldConfiguration?.bill_to_phone.value || "",

                            // address: addressFormator(
                            //   fieldConfiguration?.bill_to_address_1.value,
                            //   fieldConfiguration?.bill_to_address_2.value,
                            // ),
                            // details: addressFormator(
                            //   fieldConfiguration?.bill_to_city.value || "",
                            //   fieldConfiguration?.bill_to_state.value || "",
                            //   fieldConfiguration?.bill_to_zipcode.value || "",
                            // ),
                            // country: addressFormator(
                            //   fieldConfiguration?.bill_to_country.value || "",
                            // ),
                          }
                        }
                      />
                    </Paper>
                  </div>
                </div>
              </div>
            </Grid>

            {/* <Grid
          container
          spacing={3}
          className="paddingTop"
          alignItems="center"
          style={{
            marginLeft: 10,
            marginRight: 10,
            marginBottom: 35,
            backgroundColor: "white",
          }}
        >
          <Grid
            item
            xs={12}
            sm={12}
            md={12}
            lg={12}
            // style={{ backgroundColor: "white" }}
          >
            <Table
              actionElements={
                <Button
                  iconCls="btn_icon"
                  icon={<AddCircleOutlineOutlined />}
                  text="Add"
                  handleClick={() => {
                    fieldConfiguration.itemDetails.push({
                      item_name: "Individual Tax Service",
                      description: "",
                      description_is_custom: false,
                      quantity: "",
                      rate: "",
                      amount: 0,
                      status: 1,
                    });
                    setFieldConfiguration({ ...fieldConfiguration });
                  }}
                />
              }
              validateRef={submitValidateRef}
              cancelValidate={cancelValidateRef}
              variant="small"
              title="Add Items"
              header={[
                { id: "1", label: "Item" },
                { id: "2", label: "Description" },
                { id: "3", label: "Quantity" },
                { id: "4", label: "Rate" },
                { id: "5", label: "Amount" },
                { id: "6", label: "Action" },
              ]}
              data={fieldConfiguration.itemDetails}
              fields={defaultFields}
              hiddenFields={[
                "client_contract_item_id",
                "client_invoice_item_id",
                "status",
              ]}
              actions={["delete"]}
              // actionDefault
              onActionClick={onItemAction}
            />
          </Grid>
        </Grid> */}
            <Grid
              container
              spacing={3}
              className="paddingTop"
              alignItems="center"
              style={{
                marginLeft: 0,
                marginRight: 0,
                marginBottom: 15,
              }}
            >
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className="invoice-items-table contract-container">
                  <div
                    className="add-items-header-row"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <h1 className="card-create-invoice-title">Add Items</h1>
                    <Button
                      className="timesheet-add-row-btn"
                      variant="outline"
                      // iconCls="btn_icon"
                      icon={<AddCircleOutlineOutlined />}
                      text="Add"
                      handleClick={() => {
                        fieldConfiguration.itemDetails.push({
                          item_name: "Individual Tax Service",
                          description: "",
                          description_is_custom: false,
                          quantity: "",
                          rate: "",
                          amount: 0,
                          status: 1,
                        });
                        setFieldConfiguration({ ...fieldConfiguration });
                      }}
                    />
                  </div>
                  <div className="timesheet-table-content">
                    <Table
                      // actionElements={
                      //   <Button
                      //     iconCls="btn_icon"
                      //     icon={<AddCircleOutlineOutlined />}
                      //     text="Add"
                      //     handleClick={() => {
                      //       fieldConfiguration.itemDetails.push({
                      //         item_name: "",
                      //         description: "",
                      //         quantity: "",
                      //         rate: "",
                      //         amount: 0,
                      //         status: 1,
                      //       });
                      //       setFieldConfiguration({ ...fieldConfiguration });
                      //     }}
                      //   />
                      // }
                      // aligncss="timesheet-table-scope"
                      validateRef={submitValidateRef}
                      className="invoice-items-table-row"
                      cancelValidate={cancelValidateRef}
                      variant="small"
                      header={[
                        { id: "1", label: "Item" },
                        { id: "2", label: "Description" },
                        { id: "3", label: "Quantity" },
                        { id: "4", label: "Rate" },
                        { id: "5", label: "Amount" },
                        { id: "6", label: "Action" },
                      ]}
                      data={fieldConfiguration.itemDetails}
                      fields={defaultFields}
                      hiddenFields={[
                        "client_contract_item_id",
                        "client_invoice_item_id",
                        "description_is_custom",
                        "status",
                      ]}
                      actions={["delete"]}
                      onActionClick={onItemAction}
                    />
                  </div>
                </div>
              </Grid>
            </Grid>

            <Grid
              style={{ margin: 0, width: "100%", maxWidth: "100%" }}
              container
              spacing={3}
              className="discount-section-wrapper"
              alignItems="stretch"
            >
              <Grid item xs={12} md={6} lg={8} xl={8}>
                <Paper
                  className="discount-left-card"
                  style={{ margin: 0, backgroundColor: "white" }}
                >
                  <span className="payment-notes-msg">
                    Enter discount details. Leave a field blank to skip that
                    discount.
                  </span>
                  <div className="discount-fields-grid">
                    <div className="discount-fields-cell discountselected">
                      <Labelbox
                        type="select"
                        className="discountselect"
                        labelname="Referral discount value"
                        dropdown={DISCOUNT_REFERRAL_OPTIONS}
                        changeData={(e) =>
                          onChange(e, "discount_referral_value")
                        }
                        value={fieldConfiguration.discount_referral_value.value}
                        error={fieldConfiguration.discount_referral_value.error}
                        errmsg={
                          fieldConfiguration.discount_referral_value.errmsg
                        }
                      />
                    </div>
                    <div className="discount-fields-cell">
                      <Labelbox
                        type="text"
                        labelname="Organisation"
                        changeData={(e) => onChange(e, "organisation")}
                        value={fieldConfiguration.organisation.value}
                        error={fieldConfiguration.organisation.error}
                        errmsg={fieldConfiguration.organisation.errmsg}
                      />
                    </div>
                    <div className="discount-fields-cell">
                      <Labelbox
                        type="select"
                        className="discountselect"
                        labelname="Discount Type"
                        dropdown={[
                          { id: "Percentage", value: "Percentage" },
                          { id: "Amount", value: "Amount" },
                        ]}
                        changeData={(e) => onChange(e, "discount_type")}
                        value={fieldConfiguration.discount_type.value}
                        error={fieldConfiguration.discount_type.error}
                        errmsg={fieldConfiguration.discount_type.errmsg}
                      />
                    </div>
                    <div className="discount-fields-cell">
                      <Labelbox
                        type="text"
                        labelname="Discount Value"
                        changeData={(e) => onChange(e, "discount")}
                        value={fieldConfiguration.discount.value}
                        disabled={fieldConfiguration.discount_type.value === ""}
                        error={fieldConfiguration.discount.error}
                        errmsg={fieldConfiguration.discount.errmsg}
                      />
                    </div>
                  </div>
                  {MAIN_JSON.label === "Invoice" ? (
                    <div style={{ marginTop: 16 }}>
                      <Labelbox
                        type="textarea"
                        labelname="Invoice comments"
                        placeholder="Note the reason for a discount or any other invoice details"
                        changeData={(e) => onChange(e, "invoice_comment")}
                        value={fieldConfiguration.invoice_comment?.value || ""}
                        rows={3}
                      />
                    </div>
                  ) : null}
                </Paper>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                xl={4}
                style={{ display: "flex", width: "100%", minWidth: 0 }}
              >
                <div
                  className="discount-right-card"
                  style={{ maxWidth: "none" }}
                >
                  {/* <img
                    alt="dollar"
                    style={{ width: "95px", height: "95px" }}
                    src={Dollar}
                  /> */}

                  <InfoTable
                    titles={[
                      "Gross Total",
                      "Discount",
                      "Referral discount value",
                      "Net Total",
                    ]}
                    info={{
                      sub_total: `$ ${fieldConfiguration.sub_total.value}`,
                      discount: `${
                        fieldConfiguration.discount_status.value &&
                        fieldConfiguration.discount.value !== ""
                          ? fieldConfiguration.discount_type.value === "Amount"
                            ? `$ ${fieldConfiguration.discount.value}` // Show as amount
                            : `$ ${
                                (parseFloat(
                                  fieldConfiguration.sub_total.value || 0,
                                ) *
                                  parseFloat(
                                    fieldConfiguration.discount.value || 0,
                                  )) /
                                100
                              }` // Calculate percentage
                          : "$ 0"
                      }`,
                      "Referral discount value":
                        fieldConfiguration.discount_referral_value.value || "—",
                      total: `$ ${fieldConfiguration.total.value}`,
                    }}
                  />
                </div>
              </Grid>
            </Grid>
            {editMode.mode &&
              MAIN_JSON.label === "Invoice" &&
              Number(editMode.currentData?.status) === 4 && (
                <Grid item xs={12}>
                  <div className="invoice-cancellation-comment-readonly-wrapper">
                    <Labelbox
                      type="textarea"
                      labelname="Cancellation comment"
                      value={String(
                        editMode.currentData?.cancellation_comment ??
                          editMode.currentData?.cancellationComment ??
                          "",
                      ).trim()}
                      placeholder="—"
                      changeData={() => {}}
                      disabled
                      rows={4}
                    />
                  </div>
                </Grid>
              )}
            <Grid item xs={12}>
              <div className="hbox">
                <Button
                  text={`${editMode.mode ? "Update" : "Submit"}`}
                  large
                  disable={
                    editMode.mode &&
                    MAIN_JSON.label === "Invoice" &&
                    isInvoiceCancelled(editMode.currentData)
                  }
                  handleClick={onSubmit}
                />
                <button
                  className="employee-btn-cancel"
                  handleClick={handleCancel}
                >
                  Reset
                </button>
              </div>
            </Grid>
          </>
        )}

        {(documentContent.state || markPaidModal) && (
          <Modal
            className="timesheet-view-modal"
            open={documentContent.state || markPaidModal}
            maxWidth={!undoPaidRef.current}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2 style={{ fontWeight: "bold" }}>
                {documentContent.state
                  ? `${MAIN_JSON.label} Preview`
                  : undoPaidRef.current
                    ? `${undoPaidRef.current?.type?.capitalize()} Payment`
                    : "Payment Confirmation"}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {documentContent.state && (
                  <Button
                    text="Print"
                    variant="outline"
                    icon={<PrintRounded />}
                    handleClick={() => handlePrint()}
                  />
                )}
                <button
                  className="close-btn"
                  onClick={() => {
                    if (documentContent.state)
                      setDocumentContent({ state: false, data: {} });
                    if (markPaidModal) setMarkPaidModal(false);
                    markPaidRef.current = null;
                    undoPaidRef.current = null;
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            {documentContent.state && <View />}
            {markPaidModal && <MarkAsPaid />}
          </Modal>
        )}

        {loading && <Spinner />}
        {paymentStatusModal && (
          <PaymentStatusModal
            open={paymentStatusModal}
            handleClose={handlePaymentStatusClose}
            invoice={selectedInvoice}
          />
        )}

        {deleteDialog.open && (
          <Modal
            title={null}
            open={deleteDialog.open}
            handleClose={closeDeleteDialog}
            className="delete-confirm-modal timesheet-view-modal"
          >
            <div className="delete-confirm">
              <div className="delete-confirm-header">
                <h3>Delete</h3>
                <button
                  type="button"
                  className="delete-confirm-close"
                  onClick={closeDeleteDialog}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <p className="delete-confirm-text">
                Are you sure want delete this invoice?
              </p>
              <div className="delete-confirm-actions">
                <button
                  type="button"
                  className="delete-btn secondary"
                  onClick={closeDeleteDialog}
                >
                  No
                </button>
                <button
                  type="button"
                  className="delete-btn danger"
                  onClick={confirmDelete}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </Modal>
        )}
      </Grid>
    </div>
  );
};

export default CreatePage;
